import { Box, Collapse, Stack } from '@mui/material';
import { useGetNotifications } from 'apps/app/src/hooks/requests/use-get-notifications';
import { useToggle } from 'apps/app/src/hooks/use-toggle';
import { sortBy } from 'lodash';
import React, { useMemo } from 'react';
import { NOTIFICATION_POLLING_RATE } from './home-page-sidebar-notifications/home-page-sidebar-notifications-consts/notifications-polling-rate-const';
import { HomePageSideBarNotificationsButton } from './home-page-sidebar-notifications/HomePageSidebarNotificationsButton';
import { HomePageSideBarNotificationList } from './home-page-sidebar-notifications/HomePageSidebarNotificationsList';
import { HomePageSidebarIntegrations } from './HomePageSidebarIntegrations';
import { HomePageSidebarIntegrationsButton } from './HomePageSidebarIntegrationsButton';

const SIDEBAR_OPTIONS = Object.freeze({
  NOTIFICATIONS: 'notifications',
  INTEGRATIONS: 'integrations',
});

type SidebarOption = typeof SIDEBAR_OPTIONS[keyof typeof SIDEBAR_OPTIONS];

export const HomePageSidebar: React.FC = () => {
  const [isCollapseOpen, toggleCollapse] = useToggle(true);
  const [selectedOption, setSelectedOption] = React.useState<SidebarOption | null>(SIDEBAR_OPTIONS.NOTIFICATIONS);

  const { data: clientNotifications, isLoading: isNotificationsLoading } = useGetNotifications({ refetchInterval: NOTIFICATION_POLLING_RATE });

  const handleOptionClick = (option: SidebarOption) => {
    if (option === selectedOption || !isCollapseOpen) {
      toggleCollapse();
    }

    setSelectedOption((prevOption) => prevOption === option ? null : option);
  };

  const sortedNotifications = useMemo(() => sortBy(clientNotifications, ({ notification }) => notification.metadata.date).reverse(), [clientNotifications]);

  return (
    <Stack
      sx={theme => ({
        borderTop: `1px solid ${theme.palette.divider}`,
      })}
      direction="row"
      height="100%"
    >
      <Collapse
        in={isCollapseOpen}
        orientation="horizontal"
        sx={{
          height: '100%',
        }}
      >
        <Box
          sx={theme => ({
            width: '380px',
            minWidth: '380px',
            height: '100%',
            backgroundColor: theme.palette.background.paper,
            borderLeft: `1px solid ${theme.palette.divider}`,
          })}
        >
          {selectedOption === SIDEBAR_OPTIONS.NOTIFICATIONS && (
            <HomePageSideBarNotificationList
              isLoading={isNotificationsLoading}
              clientNotifications={sortedNotifications}
            />
          )}

          {selectedOption === SIDEBAR_OPTIONS.INTEGRATIONS && (
            <HomePageSidebarIntegrations/>
          )}

        </Box>
      </Collapse>
      <Stack 
        gap={1}
        sx={theme => ({
          minWidth: '48px',
          minHeight: '100%',
          backgroundColor: theme.palette.background.paper,
          borderLeft: `1px solid ${theme.palette.divider}`,
          alignItems: 'center',
          padding: 0.5,
        })}
      >
        <HomePageSideBarNotificationsButton
          onClick={() => handleOptionClick(SIDEBAR_OPTIONS.NOTIFICATIONS)}
          isSelected={selectedOption === SIDEBAR_OPTIONS.NOTIFICATIONS}
          notificationAmount={clientNotifications?.length ?? 0}
        />
        <HomePageSidebarIntegrationsButton 
          onClick={() => handleOptionClick(SIDEBAR_OPTIONS.INTEGRATIONS)}
          isSelected={selectedOption === SIDEBAR_OPTIONS.INTEGRATIONS}
        />
      </Stack>
    </Stack>
  );
};